import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, Store, Truck, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";

const STATUS = {
  paid: { label: "Paid", icon: CheckCircle2, cls: "text-[#2f7d4f] bg-[#2f7d4f]/10" },
  shipped: { label: "Shipped", icon: Truck, cls: "text-forest bg-forest/10" },
  fulfilled: { label: "Shipped", icon: Truck, cls: "text-forest bg-forest/10" },
  refunded: { label: "Refunded", icon: RotateCcw, cls: "text-blaze-dark bg-blaze/10" },
};

const fmt = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

export default function Orders() {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!isAuthed) { openAuth("login"); setOrders([]); return; }
    api.myOrders().then((d) => setOrders(d.orders || [])).catch(() => setOrders([]));
  }, [isAuthed, openAuth]);

  return (
    <div className="min-h-screen bg-paper" data-testid="orders-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/marketplace")} className="flex w-fit items-center gap-1.5 text-sm text-saddle hover:text-forest mb-6" data-testid="orders-back"><ArrowLeft size={15} /> Marketplace</button>
        <span className="hr-overline text-saddle">Your purchases</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-forest mt-2">My Orders</h1>

        {orders === null ? (
          <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : orders.length === 0 ? (
          <div className="mt-8 border border-dashed border-saddle/30 p-10 text-center" data-testid="orders-empty">
            <Package size={30} className="mx-auto text-saddle/40" />
            <p className="font-serif text-2xl text-forest mt-3">No orders yet.</p>
            <p className="text-charcoal/60 text-sm mt-1">Gear and hunts you buy on HomeRange will show up here.</p>
            <button onClick={() => navigate("/marketplace")} className="mt-5 bg-forest text-bone px-5 py-2.5 text-sm font-semibold hover:bg-forest-2 transition-colors" data-testid="orders-shop">Browse the marketplace</button>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="orders-list">
            {orders.map((o) => {
              const st = STATUS[o.refund_status === "refunded" ? "refunded" : o.order_status] || STATUS.paid;
              const Icon = st.icon;
              const total = (o.gross_cents || 0) + (o.shipping_cents || 0);
              return (
                <motion.div key={o.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-saddle/20 p-4 flex gap-4" data-testid="order-row">
                  <div className="w-16 h-16 shrink-0 bg-bone/60 overflow-hidden grid place-items-center">
                    {o.listing_image ? <img src={o.listing_image} alt="" className="w-full h-full object-cover" /> : <Package size={20} className="text-saddle/40" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-serif text-lg text-forest truncate">{o.listing_title || "Order"}</h3>
                        <span className="text-xs text-charcoal/55 inline-flex items-center gap-1"><Store size={12} /> {o.business_name}</span>
                      </div>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase px-2 py-1 shrink-0 ${st.cls}`} data-testid={`order-status-${o.id}`}><Icon size={12} /> {st.label}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-sm">
                      <span className="text-charcoal/50 text-xs">
                        {fmtDate(o.created_at)}
                        {o.variant_label && ` · ${o.variant_label}`}
                        {o.shipping_cents > 0 && ` · Shipping ${fmt(o.shipping_cents)}`}
                        {o.shipping_days && o.listing_type === "product" && ` · ${o.shipping_days}`}
                      </span>
                      <span className="font-serif text-xl text-forest tabular-nums">{fmt(total)}</span>
                    </div>
                    {o.tracking_number && (
                      <div className="mt-2 text-xs bg-forest/5 text-forest px-2 py-1 inline-flex items-center gap-1.5" data-testid={`order-tracking-${o.id}`}>
                        <Truck size={12} /> Tracking: {o.carrier ? `${o.carrier} ` : ""}{o.tracking_number}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
